import { ChangeDetectionStrategy, Component, effect, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectButton } from 'primeng/selectbutton';
import { RolesCatalogo } from './roles-catalogo/roles-catalogo';
import { PerfisCatalogo } from './perfis-catalogo/perfis-catalogo';
import { PerfilDetalhe } from './perfil-detalhe/perfil-detalhe';
import { AtribuirRoles } from './atribuir-roles/atribuir-roles';
import { PermissoesDiagnostico } from './permissoes-diagnostico/permissoes-diagnostico';
import { PermissionService, PERMISSOES } from '@/core/permissions';

@Component({
    selector: 'app-permissoes',
    standalone: true,
    imports: [FormsModule, SelectButton, RolesCatalogo, PerfisCatalogo, PerfilDetalhe, AtribuirRoles, PermissoesDiagnostico],
    templateUrl: './permissoes.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Shell das permissões: alterna entre catálogo de roles, atribuição a usuário e diagnóstico. */
export class Permissoes {
    private readonly perm = inject(PermissionService);
    private readonly perfisCatalogo = viewChild(PerfisCatalogo);

    private readonly P = PERMISSOES.configuracoes.usuarios.permissoes;

    /** Catálogo de roles e Diagnóstico são do CFM (`somenteCfm`); Perfis e Atribuir abrem para a UF. */
    protected get podeCatalogo(): boolean {
        return this.perm.can(this.P.catalogo);
    }
    protected get podeAdministrar(): boolean {
        return this.perm.can(this.P.administrar);
    }
    protected get podeDiagnostico(): boolean {
        return this.perm.can(this.P.diagnostico);
    }

    protected get tabs() {
        const abas: { name: string; value: string }[] = [];
        if (this.podeCatalogo) abas.push({ name: 'Catálogo de Roles', value: 'catalogo' });
        if (this.podeAdministrar) abas.push({ name: 'Perfis', value: 'perfis' }, { name: 'Atribuir a Usuário', value: 'atribuir' });
        if (this.podeDiagnostico) abas.push({ name: 'Diagnóstico', value: 'diagnostico' });
        return abas;
    }

    /**
     * Aba ativa. Começa vazia e é resolvida no construtor para a primeira aba disponível: um
     * operador de UF não tem "Catálogo de Roles", e um valor fixo abriria a tela num estado vazio.
     */
    protected readonly aba = signal('');

    /** `undefined` = fechado; `null` = editor em modo novo; número = editando aquele id. */
    protected readonly perfilEmEdicao = signal<number | null | undefined>(undefined);

    /**
     * O catálogo de perfis fica sempre montado (o shell alterna abas com `[hidden]`, não `@if`), então
     * entrar na aba "Perfis" não passa pelo `ngOnInit` dele — sem isto, o contador de usuários por perfil
     * (alterado em "Atribuir a Usuário") ficava desatualizado até um F5. Dispara só na transição PARA
     * 'perfis' (não a cada change detection) e ignora a passagem inicial do effect: o próprio `ngOnInit`
     * do catálogo já carrega uma vez ao montar, então recarregar aqui também duplicaria a requisição.
     */
    constructor() {
        this.aba.set(this.tabs[0]?.value ?? '');

        let abaAnterior = this.aba();
        effect(() => {
            const abaAtual = this.aba();
            if (abaAtual === 'perfis' && abaAnterior !== 'perfis') {
                this.perfisCatalogo()?.recarregar();
            }
            abaAnterior = abaAtual;
        });
    }

    protected onAbrirEditorPerfil(id: number | null): void {
        this.perfilEmEdicao.set(id);
    }
    /** Perfil salvo com sucesso: fecha o editor e recarrega o catálogo para refletir a mudança. */
    protected onPerfilSalvo(): void {
        this.perfilEmEdicao.set(undefined);
        this.perfisCatalogo()?.recarregar();
    }
    protected onPerfilFechado(): void {
        this.perfilEmEdicao.set(undefined);
    }
}
